
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  Users,
  Target,
  Activity,
  BarChart3
} from 'lucide-react';

interface SessionMetrics {
  totalShots: number;
  averageDistance: number;
  sessionDuration: number;
  activeParticipants: number;
}

interface SessionMetricsProps {
  metrics: SessionMetrics;
}

export function SessionMetrics({ metrics }: SessionMetricsProps) {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  const getPerformanceLevel = (avgDistance: number) => {
    if (avgDistance >= 70) return { level: 'Excellent', color: 'bg-green-500' };
    if (avgDistance >= 50) return { level: 'Good', color: 'bg-yellow-500' };
    return { level: 'Developing', color: 'bg-red-500' };
  };

  const performance = getPerformanceLevel(metrics.averageDistance);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Session Overview
        </CardTitle>
        <CardDescription>
          Real-time session performance metrics
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Duration */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Session Duration</p>
              <p className="text-xs text-muted-foreground">Active training time</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-blue-600">
              {formatDuration(metrics.sessionDuration)}
            </p>
          </div>
        </div>

        {/* Active Participants */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-full">
              <Users className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Active Players</p>
              <p className="text-xs text-muted-foreground">Currently training</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-green-600">
              {metrics.activeParticipants}
            </p>
          </div>
        </div>

        {/* Total Shots */}
        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-full">
              <Activity className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Total Shots</p>
              <p className="text-xs text-muted-foreground">Session total</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-purple-600">
              {metrics.totalShots}
            </p>
            <p className="text-xs text-muted-foreground">
              {metrics.activeParticipants > 0 ? 
                `${Math.round(metrics.totalShots / metrics.activeParticipants)} avg/player` : 
                '0 avg/player'
              }
            </p>
          </div>
        </div>

        {/* Average Distance */}
        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-full">
              <Target className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Avg Distance</p>
              <p className="text-xs text-muted-foreground">Session average</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-orange-600">
              {metrics.averageDistance}m
            </p>
            <Badge variant="outline" className={`text-xs ${performance.color} text-white border-none`}>
              {performance.level}
            </Badge>
          </div>
        </div>

        {/* Performance Indicator */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Session Performance</span>
            <span className="text-sm text-muted-foreground">{performance.level}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${performance.color}`}
              style={{ 
                width: `${Math.min((metrics.averageDistance / 100) * 100, 100)}%` 
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0m</span>
            <span>50m</span>
            <span>100m+</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-3">Quick Stats</h4>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-muted-foreground">Shots/minute</p>
              <p className="font-medium">
                {metrics.sessionDuration > 0 ? 
                  (metrics.totalShots / metrics.sessionDuration).toFixed(1) : 
                  '0'
                }
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Players/total</p>
              <p className="font-medium">{metrics.activeParticipants}/4</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}